import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState, } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography, } from '@mui/material';
// import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useStore, useTokens, } from '@mcn-platform/models';
import loadable from '@loadable/component';
import { clone, cloneDeep, sampleSize } from 'lodash-es';
import AssetImage from '../../common/asset/AssetImage';
import AssetVideoPlayer from '../../common/asset/AssetVideoPlayer';
import CustomButton from '../../common/CustomButton';
import Quiz from './Quiz';
// import QuizResult from './QuizResult';
import { WysiwygViewer } from '../../../publisher/wysiwyg/WysiwygEditor';
import { runQuizAttemptCompletionCallbacks } from './quizAttemptCompletion';
const QuizResult = loadable(() => import('./QuizResult'));
const formatTime = (seconds) => {
    if (seconds <= 0) {
        return '-';
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(remainingSeconds).padStart(2, '0');
    return `${formattedMinutes}:${formattedSeconds}`;
};
const QuizPanel = ({ courseItem, progress, onEnd, onAttemptComplete,
// refreshProgress,
 }) => {
    const store = useStore();
    const tokens = useTokens();
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(-1);
    const [displayMode, setDisplayMode] = useState('intro');
    const [endTime, setEndTime] = useState(null);
    const [timeRemaining, setTimeRemaining] = useState(0);
    const [isTimeExceedeModalOpen, setTimeExceedeModalOpen] = useState(false);
    const [questionsToAsk, setQuestionsToAsk] = useState([]);
    const [blockInput, setBlockInput] = useState(false);
    const { item: quizItem } = courseItem;
    const { failedMessage, instructions, limitRetakes, maximumRetakes, noOfQuestionsToAsk, passedMessage, passingPercentage, posterImage, randomizeQuestionOrder, id: quizID, questions, showAnswers, timeLimitEnabled, timeLimitForQuiz, } = quizItem;
    const quizResults = progress?.quizResults;
    const quizResult = quizResults?.[quizID];
    const noOfTimeQuizTaken = quizResult?.timesTaken || 0;
    // console.log('quizResult', quizResult);
    const finishQuiz = useCallback((newQuizResult) => {
        const score = Math.floor((newQuizResult.answers.correct.length
            / questionsToAsk.length)
            * 100);
        newQuizResult.isCompleted = true;
        newQuizResult.score = score;
        newQuizResult.completedAt = new Date();
        newQuizResult.type = 'quiz';
        newQuizResult.isPassed = score >= passingPercentage;
        setEndTime(null); // turns off timer
    }, [passingPercentage, questionsToAsk.length]);
    const saveResults = useCallback(async (newQuizResults) => {
        const getProgress = () => progress;
        const retryOperation = async (operation, operationName, retries = 3, delay = 100) => {
            try {
                await operation();
            }
            catch (error) {
                if (retries > 0) {
                    console.log(`Error ${operationName}, retrying... (${retries} attempts left)`);
                    await new Promise((resolve) => { setTimeout(resolve, delay); });
                    await retryOperation(operation, operationName, retries - 1);
                }
                else {
                    console.error(`Failed to ${operationName} after multiple retries:`, error);
                    throw error;
                }
            }
        };
        const saveOperation = async () => {
            const latestProgress = getProgress();
            latestProgress.updateValues({ quizResults: newQuizResults });
            // TODO - if progress has a course field then its a courseProgres,
            // otherwise programProgress - use better logic for this
            const { success, reason } = await store.update((latestProgress.course ? 'courseProgress' : 'programProgress'), latestProgress);
            if (!success) {
                if (reason === 'concurrency') {
                    // The concurrency error message was already shown by RootModel.
                    // Optionally, refresh UI or notify user.
                    return;
                }
                throw Error('Failed to save quiz results');
            }
        };
        await retryOperation(saveOperation, 'save quiz results');
    }, [store, progress]);
    const updateQuizProgress = useCallback(async (answer, timeExpired = false) => {
        setBlockInput(true);
        const newQuizResult = cloneDeep(quizResults[quizID]);
        const { answers, results } = newQuizResult;
        if (answer) {
            if (answer.isCorrect) {
                answers.correct.push(answer.question);
            }
            else {
                answers.incorrect.push(answer.question);
            }
            results.push({
                question: answer.question.id,
                isCorrect: answer.isCorrect,
                userAnswers: answer.userAnswers,
                inputValue: answer.inputValue,
            });
        }
        const newQuizResults = clone(quizResults);
        newQuizResults[quizID] = newQuizResult;
        if (timeExpired || currentQuestionIndex === questionsToAsk.length - 1) {
            finishQuiz(newQuizResult);
        }
        await saveResults(newQuizResults);
        // Correctness boundary: persist the attempt marker before the pass-only
        // onEnd path triggers its second progress invocation.
        await runQuizAttemptCompletionCallbacks(newQuizResult, onAttemptComplete, onEnd);
        setBlockInput(false);
    }, [
        currentQuestionIndex,
        finishQuiz,
        onEnd,
        onAttemptComplete,
        questionsToAsk.length,
        quizID,
        quizResults,
        saveResults,
    ]);
    const nextQuestion = async () => {
        if (currentQuestionIndex === questionsToAsk.length - 1) {
            setDisplayMode('results');
        }
        else {
            setDisplayMode('question');
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        }
    };
    const handleAnswer = (answer) => {
        updateQuizProgress(answer, false);
        if (showAnswers) {
            setDisplayMode('answer');
        }
        else {
            nextQuestion();
        }
    };
    useEffect(() => {
        if (!endTime) {
            return;
        }
        setTimeout(() => {
            if (endTime) {
                const newTimeRemaining = (endTime.getTime() - Date.now()) / 1000;
                if (newTimeRemaining <= 0) {
                    setTimeExceedeModalOpen(true);
                    updateQuizProgress(null, true);
                }
                setTimeRemaining(newTimeRemaining);
            }
        }, 500);
    }, [endTime, timeRemaining, updateQuizProgress]);
    const startQuiz = async () => {
        if (!(progress)) {
            console.log('Missing progress record, cant do quiz');
            return;
        }
        const history = clone(quizResult?.history || []);
        if (quizResult?.answers) {
            // quiz has been taken previously, move the answers
            // into a history array
            history.push({
                answers: quizResult.answers,
                isCompleted: quizResult.isCompleted,
                isPassed: quizResult.isPassed,
                startedDate: quizResult.startedDate,
                score: quizResult.score,
                progress: quizResult.progress,
                completedAt: quizResult.completedAt,
                results: quizResult.results,
            });
        }
        setDisplayMode('question');
        setCurrentQuestionIndex(0);
        const newQuestionsToAsk = randomizeQuestionOrder
            ? sampleSize(questions, noOfQuestionsToAsk || questions.length)
            : questions;
        setQuestionsToAsk(newQuestionsToAsk);
        if (timeLimitEnabled) {
            const newEndTime = new Date();
            newEndTime.setSeconds(newEndTime.getSeconds() + (timeLimitForQuiz * 60));
            setEndTime(newEndTime);
        }
        const newQuizResult = {
            timesTaken: (quizResult?.timesTaken || 0) + 1,
            answers: {
                correct: [],
                incorrect: [],
            },
            results: [],
            isStarted: true,
            history,
        };
        // this is the quizResult for this quiz
        // TODO - data manipulation belongs in the model
        const newQuizResults = clone(quizResults);
        newQuizResults[quizID] = newQuizResult;
        await saveResults(newQuizResults);
    };
    const handleClose = () => {
        setTimeExceedeModalOpen(false);
        setDisplayMode('results');
    };
    const retakeQuiz = () => {
        setDisplayMode('intro');
    };
    const currentQuestion = questionsToAsk?.[currentQuestionIndex];
    // dont limit retakes at all or under the maximum number
    const isRetakeAllowed = (!limitRetakes)
        || noOfTimeQuizTaken <= maximumRetakes;
    return (_jsxs(Box, { sx: {
            p: 2,
            minHeight: 400,
        }, children: [_jsxs(Box, { sx: {
                    mx: 4,
                }, children: [displayMode === 'intro' && (_jsxs(Box, { sx: { position: 'relative' }, children: [_jsx(Box, { onClick: startQuiz, children: _jsx(AssetImage, { asset: posterImage, size: "thumb400w", customStyle: {
                                        maxHeight: '300px',
                                        width: '100%',
                                        objectFit: 'cover',
                                    } }) }), instructions && (_jsxs(_Fragment, { children: [_jsx(Typography, { component: "div", variant: "subtitle1", fontWeight: "fontWeightBold", children: "Instructions:" }), _jsx(Box, { className: "slate", sx: { ml: 2 }, children: _jsx(WysiwygViewer, { id: "id", value: instructions.slate }) })] })), isRetakeAllowed && (_jsx(Box, { display: "flex", alignItems: "center", justifyContent: "center", children: _jsx(CustomButton, { label: tokens.getToken('startQuizButtonLabel'), onClick: startQuiz, customStyle: { flexGrow: 0, minWidth: 110, mt: 0 } }) })), !isRetakeAllowed && (_jsx(Typography, { className: "systemPagesMessage", sx: {
                                    textAlign: 'center',
                                }, children: "You have exceeded the number of times you are allowed to take this quiz or test." }))] })), ['question', 'answer'].includes(displayMode) && (_jsxs(Box, { sx: { px: '10%' }, children: [_jsxs(Stack, { direction: "row", sx: { width: 1 }, children: [_jsx(Typography, { component: "div", variant: "subtitle1", fontWeight: "fontWeightBold", sx: { width: 0.5 }, children: `Question ${currentQuestionIndex + 1} of ${questionsToAsk.length}` }), timeLimitEnabled && endTime && (_jsxs(Typography, { component: "div", variant: "subtitle1", fontWeight: "fontWeightBold", sx: {
                                            textAlign: 'right',
                                            width: 0.5,
                                        }, children: ["Time Remaining:", ' ', formatTime(timeRemaining)] }))] }), currentQuestion?.videoID ? (_jsx(Box, { sx: { textAlign: 'center', mb: 2, width: '100%' }, children: _jsx(AssetVideoPlayer, { asset: store.getOrLoad('asset', currentQuestion.videoID), aspectRatio: "16:9", assetPoster: currentQuestion.posterImageID
                                        ? store.getOrLoad('asset', currentQuestion.posterImageID)
                                        : undefined, seekPosition: undefined, seekTrigger: undefined, onProgress: () => { }, onEnd: () => (false), onPause: () => { }, onDuration: () => { }, handleNextPrevious: () => { }, disableSeekAhead: false }) })) : currentQuestion?.posterImageID ? (
                            // Show image if no video
                            _jsx(Box, { sx: { textAlign: 'center', mb: 2, width: '100%' }, children: _jsx(AssetImage, { asset: store.getOrLoad('asset', currentQuestion.posterImageID), size: "original", customStyle: {
                                        maxHeight: '300px',
                                        objectFit: 'cover',
                                    } }) })) : null, _jsx(Quiz, { question: currentQuestion, nextQuestion: nextQuestion, setCourseResult: handleAnswer, isKnowledgeCheck: false, showAnswer: Boolean(displayMode === 'answer'), blockInput: blockInput }, currentQuestion?.id)] })), displayMode === 'results' && (_jsxs(Box, { children: [_jsx(Box, { className: "slate", sx: { m: 2 }, children: _jsx(WysiwygViewer, { id: "id", value: quizItem.postQuizMessage?.slate }) }), quizItem.showScoreOnCompletion && (_jsxs(_Fragment, { children: [_jsx(Typography, { component: "div", variant: "h4", fontWeight: "fontWeightBold", children: "Quiz Results" }), _jsx(QuizResult, { score: quizResult?.score }), _jsx(Typography, { className: "systemPagesMessage", sx: {
                                            textAlign: 'center',
                                            py: 4,
                                        }, children: quizResult?.isPassed ? passedMessage : failedMessage })] })), isRetakeAllowed && (_jsx(Box, { display: "flex", alignItems: "center", justifyContent: "center", children: _jsx(CustomButton, { label: "Retake Quiz", onClick: retakeQuiz, customStyle: { flexGrow: 0, minWidth: 100, mt: 1 } }) })), !isRetakeAllowed && (_jsx(Typography, { className: "systemPagesMessage", sx: {
                                    textAlign: 'center',
                                }, children: "You have reached the maximum number of retakes allowed." }))] }))] }), _jsxs(Dialog, { open: isTimeExceedeModalOpen, PaperProps: { sx: { minWidth: '50%' } }, children: [_jsx(DialogTitle, { children: "Time Limit Exceeded" }), _jsx(DialogContent, { sx: { mx: 3 }, children: _jsx(Typography, { variant: "subtitle1", component: "div", children: "The time limit has been exceeded." }) }), _jsx(DialogActions, { children: _jsx(Button, { onClick: handleClose, children: "Ok" }) })] })] }));
};
export default observer(QuizPanel);
//# sourceMappingURL=QuizPanel.js.map